import { useCallback, useEffect, useMemo, useState } from "react";

export interface SelectableItem {
  id: string;
}

const useSelection = <T extends SelectableItem>(items: T[]) => {
  const [selection, setSelection] = useState<Set<string>>(new Set<string>([]));

  const isSelected = useCallback(
    (id: string) => selection.has(id),
    [selection],
  );

  const toggleSelection = useCallback((id: string) => {
    setSelection((selection) => {
      const newSelection = new Set(selection);
      if (selection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  }, []);

  const toggleAllSelections = useCallback(() => {
    setSelection((selection) => {
      const areAllSelected = selection.size === items.length;

      if (areAllSelected) {
        return new Set<string>();
      } else {
        return new Set(items.map((i) => i.id));
      }
    });
  }, [items]);

  const isAllSelected = useMemo(() => {
    return selection.size !== 0 && selection.size === items.length;
  }, [items, selection]);

  const isAnySelected = useMemo(() => {
    return selection.size !== 0;
  }, [items, selection]);

  useEffect(() => {
    // check if items and current selection are in sync
    // for example when removing item
    const dataIds = items.map((d) => d.id);
    setSelection((selection) => {
      const newSelection = new Set(selection);
      selection.forEach((item) => {
        if (!dataIds.includes(item)) {
          newSelection.delete(item);
        }
      });
      return newSelection;
    });
  }, [items]);

  const clearSelection = useCallback(() => {
    setSelection(new Set<string>());
  }, []);

  const selectedItems = useMemo(() => {
    const selectedItems: T[] = [];
    selection.forEach((id) => {
      const findItem = items.find((d) => d.id === id);
      if (findItem) {
        selectedItems.push(findItem);
      }
    });
    return selectedItems;
  }, [items, selection]);

  return {
    selection: Array.from(selection),
    isSelected,
    toggleSelection,
    toggleAllSelections,
    isAllSelected,
    isAnySelected,
    clearSelection,
    selectedItems,
    totalSelected: selection.size,
  };
};

export default useSelection;
